import * as React from 'react'
import * as Redux from 'react-redux'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import * as shadow from '@owl-nest/shadow'

import * as plume from '@ulule/owl-kit-components/next'

import { Like } from './Like'
import * as projectModel from '../../../models/project'
import { RootState } from '../../../../reducers'
import { SuccessMessage } from './SuccessMessage'
import { SplashTitle } from '../components/SplashTitle'
import { Title } from './Title'
import { likeProject } from '../../../../actions/project'

type HomeProps = {
  iframe: boolean
}

export function Home({ iframe }: HomeProps): React.ReactElement<HomeProps> {
  const dispatch = Redux.useDispatch()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const defaultFollowers = [
    api.UserRole.Editor,
    api.UserRole.Fan,
    api.UserRole.Moderator,
    api.UserRole.Owner,
    api.UserRole.Supporter,
  ]
  const auth = useAuth()
  const tracking = shadow.useTracking()

  const [isUserFan, setIsUserFan] = React.useState(!!project.user_role && defaultFollowers.includes(project.user_role))
  const [isLoading, setIsLoading] = React.useState(false)

  const showTitle = auth.type !== 'existing' && !isLoading && !isUserFan

  return (
    <HomeWrapper $iframe={iframe}>
      {showTitle && <Title />}

      <Block>
        {iframe ? (
          <Like onLoggedIn={onLoggedIn} onLike={onLike} />
        ) : isLoading ? (
          <CenterSpinner>
            <plume.Spinner />
          </CenterSpinner>
        ) : isUserFan ? (
          <SuccessMessage auth={auth} />
        ) : (
          <Like onLoggedIn={onLoggedIn} onLike={onLike} />
        )}
      </Block>
    </HomeWrapper>
  )

  async function onLike(): Promise<void> {
    if (auth.type === 'loggedin' || auth.type === 'loggedin-as-guest') {
      setIsLoading(true)
      await like()
      setIsLoading(false)
    }
  }

  async function onLoggedIn(user: api.AuthenticatedUser<any>): Promise<void> {
    setIsLoading(true)
    await like(user)
    setIsLoading(false)
  }

  async function like(user?: api.AuthenticatedUser<any>): Promise<void> {
    await dispatch(likeProject(project.id))
    setIsUserFan(true)

    if (user?.is_guest) {
      tracking.social.track({
        eventLabel: `source_coming_soon_guest_user`,
        socialAction: 'Like',
        socialNetwork: shadow.SOCIAL_NETWORK.ULULE,
        socialTarget: project.absolute_url,
      })
    } else {
      tracking.social.track({
        eventLabel: `source_coming_soon_known_user`,
        socialAction: 'Like',
        socialNetwork: shadow.SOCIAL_NETWORK.ULULE,
        socialTarget: project.absolute_url,
      })
    }

    if (auth.type === 'loggedin-as-guest') {
      // We logout the user here because guest user haven't acces and it makes errors
      // on other pages or on refresh.
      // We just need a logged user for handle like and logout him after it's ok.
      auth.logout()
    }
  }
}

const Block = styled.div`
  background-color: ${plume.COLORS.PRIMARY_WHITE};
  border-radius: 4px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-bottom: 30px;
  }
`

const CenterSpinner = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 186px;
  text-align: center;
  width: 100%;
`

const HomeWrapper = styled.div<{ $iframe: boolean }>`
  padding: 0 16px 16px;

  ${({ $iframe }) => {
    if (!$iframe) {
      return css`
        @media screen and ${plume.BREAKPOINTS.LAPTOP} {
          width: 884px;
        }
      `
    }
  }}
  ${SplashTitle} {
    margin-bottom: 25px;
  }

  ${plume.styles.heading.S} {
    margin: 0 auto;
    margin-bottom: 25px;
    text-align: center;
    width: fit-content;
  }
`
